import { useComputed } from '@preact/signals';
import type { JSX } from 'preact';
import {
  Icon,
  Button,
  type ButtonProps
} from '@nespresso-design-system/components';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand';
import useQueryStringStore from '@/widgets/hooks/useQueryStringStore';
import { type PersistedState } from '@/widgets/ProductList/Store';
import useTranslations from '@/widgets/hooks/useTranslations';
import styles from './TechnologyInfoDrawer.module.scss';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions';
import type { Options } from '@/widgets/ProductList/config';
import { trackPlpInteraction } from '@/widgets/services/TrackingService.ts';

const MODAL_NAME: PersistedState['openModal'] = 'technology-info';

export default function TechnologyInfoDrawer() {
  const { technologyInfo } = useWidgetOptions<Options>();

  const [{ openModal }, setPersistedState] =
    useQueryStringStore<PersistedState>();
  const close = () => setPersistedState({ openModal: undefined });
  const isOpen = useComputed(() => openModal.value === MODAL_NAME);

  if (!technologyInfo?.title) return null;

  return (
    <ModalOnDemand
      closeBtnA11yLabel={technologyInfo.title}
      isOpen={isOpen.value}
      size="medium"
      position="side"
      class={styles.modal}
      heading={technologyInfo.title}
      onClose={close}
    >
      <p>{technologyInfo.content}</p>
      <div class={styles.capsuleOptions}>
        {technologyInfo.capsuleOptions.map((option) => (
          <div key={option.type} class={styles.capsuleCard}>
            <div class={styles.capsuleTitle}>{option.type}</div>
            <img
              src={option.image}
              alt={option.type}
              class={styles.capsuleImage}
            />
            <ul class={styles.featureList}>
              {option.features.map((feature) => (
                <li
                  key={feature.text}
                  class={
                    feature.available ? styles.available : styles.unavailable
                  }
                >
                  <Icon
                    icon={
                      feature.available
                        ? `24/symbol/status-success`
                        : `24/symbol/status-canceled`
                    }
                  />
                  {feature.text}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div class={styles.cta}>
        {technologyInfo.capsuleOptions.map((option) => (
          <Button
            key={`cta-btn-${option.type}`}
            className={styles.ctaButton}
            variant="secondary"
            href={option.buttonHref}
            label={option.buttonLabel}
            onClick={(e: JSX.TargetedEvent<HTMLButtonElement>) => {
              e.preventDefault();
              trackPlpInteraction({
                actionType: `what is the difference - buy ${option.type} click`
              });
              window.location.href = option.buttonHref;
            }}
            iconPosition="leading"
          >
            {option.buttonLabel}
          </Button>
        ))}
      </div>
    </ModalOnDemand>
  );
}

type TechnologyInfoButtonProps = {
  toggle?: () => void;
};

export function TechnologyInfoButton({ toggle }: TechnologyInfoButtonProps) {
  const { technologyInfo } = useWidgetOptions<Options>();
  const { t } = useTranslations();
  const [, setPersistedState] = useQueryStringStore<PersistedState>();
  const btnInfo = t('productList.filters.info.technologies');

  if (!technologyInfo?.title) return null;

  return (
    <Button
      aria-label={btnInfo}
      variant="ghost"
      label={btnInfo}
      className={styles.infoBtn}
      icon="16/symbol/information"
      iconPosition="leading"
      size={'small' as ButtonProps['size']}
      onClick={() => {
        trackPlpInteraction({
          actionType: 'what is the difference link click'
        });
        setPersistedState({ openModal: MODAL_NAME });

        if (toggle) toggle();
      }}
    />
  );
}
